/**
 * Notifications — what has happened since you last looked.
 *
 * Josh, 2026-09-06: "We should probably also have a notifications center."
 *
 * ⚠️ IT IS DELIBERATELY NOT THE REQUESTS CENTER AGAIN. That page answers "what
 * am I waiting on" and a row lives there until a carrier answers. This answers
 * "what changed", newest first, across every programme — and an item stops
 * being interesting the moment you have read it. Built as a second copy of the
 * outstanding list it would be noise, and a noisy bell is one nobody looks at.
 *
 * ⚠️ MARKING SEEN HAPPENS ON OPEN, NOT ON A BUTTON. The question a badge
 * answers is "anything new?", and opening the page IS the answer. A "mark all
 * read" control would be a second thing to remember for no gain — and nothing
 * disappears when it clears, so there is no cost to it happening automatically.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { ApiError, api, type Notification } from '../lib/api';

const ICON: Record<Notification['kind'], string> = {
  reply: 'M4 5h16v12H7l-3 3z',
  overdue: 'M12 4l9 16H3zM12 10v4M12 17h.01',
  exhausted: 'M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z',
  unmatched: 'M4 5h16v14H4zM4 7l8 6 8-6',
  /* A signature on a line, for the one event here that is good news. */
  signed: 'M3 17c3-1 5-9 8-9s2 6 4 6 3-2 6-3M4 21h16',
};

/*
  ⚠️ GREEN, AND IT IS THE ONLY GREEN IN THE LIST. Everything else in this centre
  is a warning of some shade, so somebody scanning it reads colour before words.
  A BOR coming back signed is the one line that means something went right, and
  in amber it would be buried among the things it is least like.
*/
const TONE: Record<Notification['kind'], string> = {
  reply: 'is-reply',
  overdue: 'is-overdue-n',
  exhausted: 'is-exhausted',
  unmatched: 'is-unmatched',
  signed: 'is-signed',
};

/** "3 hours ago", "yesterday" — relative, because that is how events read. */
function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function Notifications(): JSX.Element {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[] | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api
      .notifications()
      .then((r) => {
        setItems(r.notifications);
        // Opening the page is the acknowledgement. Fire and forget: a badge that
        // fails to clear is a nuisance, not a reason to show an error.
        void api.notificationsSeen().catch(() => {});
      })
      .catch((e: unknown) => {
        setItems([]);
        setErr(e instanceof ApiError ? e.message : 'Could not load notifications.');
      });
  }, []);

  const loading = items === null;
  const list = useMemo(() => items ?? [], [items]);

  return (
    <div className="wrap">
      <PageHero
        title="Notifications"
        sub="What has happened since you last looked — replies, requests going past their date, and anything that needs a person."
      />

      {err && <div className="empty">{err}</div>}

      {loading ? (
        <p className="lr-empty">Loading…</p>
      ) : !list.length ? (
        <p className="lr-empty">
          Nothing to report.
          <span>
            A carrier replying, a request going past its date, or a chase running out will appear
            here.
          </span>
        </p>
      ) : (
        <ul className="nt-list">
          {list.map((n) => (
            <li key={n.id} className={n.unread ? 'is-new' : ''}>
              <button type="button" onClick={() => navigate(n.href)}>
                <span className={`nt-icon ${TONE[n.kind]}`} aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d={ICON[n.kind]} />
                  </svg>
                </span>
                <span className="nt-body">
                  <b>{n.title}</b>
                  <span className="nt-detail">{n.detail}</span>
                </span>
                <span className="nt-when">
                  {ago(n.at)}
                  {/* The dot, not the word "new" — it survives a narrow column. */}
                  {n.unread && <i className="nt-dot" aria-label="New" />}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <section className="panel lr-about">
        <div>
          <h3>What shows up here</h3>
          <ul>
            <li>A carrier replied to something you sent</li>
            <li>A loss run has gone a fortnight with no answer</li>
            <li>A reported loss is still unacknowledged after three days</li>
            <li>Coverage has incepted with no binder recorded</li>
            <li>A request has been chased three times — that one needs a phone call</li>
          </ul>
        </div>
        <div>
          <h3>How it differs from the Requests Center</h3>
          <p>
            The Requests Center is the standing list of everything you are waiting on. This is what
            has <i>changed</i>, newest first, and it clears once you have read it. An automatic
            reply from a carrier never appears here — it is not somebody answering.
          </p>
        </div>
      </section>
    </div>
  );
}
